import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Table, TableHead, TableRow,
  TableCell, TableBody, TableContainer, CircularProgress,
  Chip, Stack, TextField, MenuItem, Button, Dialog,
  DialogTitle, DialogContent, DialogActions, IconButton, Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { payrollApi } from '../../../api/payrollApi';
import {
  TaxDeclarationDto,
  TaxDeclarationStatus,
  TaxDeclarationStatusLabels,
  TaxDeclarationStatusColors,
} from '../../../types/payrollTypes';

const TAX_SECTIONS = [
  { value: '80C', label: '80C - Investments (PPF, ELSS, LIC, etc.)', maxLimit: 150000 },
  { value: '80D', label: '80D - Medical Insurance', maxLimit: 75000 },
  { value: '80E', label: '80E - Education Loan Interest', maxLimit: 0 },
  { value: '80G', label: '80G - Donations', maxLimit: 0 },
  { value: '80TTA', label: '80TTA - Savings Account Interest', maxLimit: 10000 },
  { value: 'HRA', label: 'HRA Exemption', maxLimit: 0 },
  { value: '24B', label: '24(b) - Home Loan Interest', maxLimit: 200000 },
  { value: '80CCD', label: '80CCD(1B) - NPS', maxLimit: 50000 },
];

interface DeclarationItem {
  section: string;
  description: string;
  declaredAmount: number;
}

const MyTaxDeclarationsPage: React.FC = () => {
  const [declarations, setDeclarations] = useState<TaxDeclarationDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [financialYear, setFinancialYear] = useState<string>('');
  const [formOpen, setFormOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formFY, setFormFY] = useState<string>('');
  const [items, setItems] = useState<DeclarationItem[]>([{ section: '80C', description: '', declaredAmount: 0 }]);

  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const defaultFY = currentMonth >= 4 ? `${currentYear}-${currentYear + 1}` : `${currentYear - 1}-${currentYear}`;
  const fyOptions = [...Array(5)].map((_, i) => {
    const y = currentYear - 2 + i;
    return `${y}-${y + 1}`;
  });

  const loadDeclarations = async () => {
    setLoading(true);
    try {
      const data = await payrollApi.getMyTaxDeclarations(financialYear || undefined);
      setDeclarations(data);
    } catch {
      toast.error('Failed to load tax declarations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadDeclarations();
  }, [financialYear]);

  const handleAddItem = () => {
    setItems([...items, { section: '80C', description: '', declaredAmount: 0 }]);
  };

  const handleRemoveItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handleItemChange = (index: number, field: keyof DeclarationItem, value: string | number) => {
    const updated = [...items];
    updated[index] = { ...updated[index], [field]: value };
    setItems(updated);
  };

  const handleOpenForm = () => {
    setFormFY(defaultFY);
    setItems([{ section: '80C', description: '', declaredAmount: 0 }]);
    setFormOpen(true);
  };

  const handleSave = async () => {
    if (!formFY) {
      toast.error('Please select a financial year');
      return;
    }
    const validItems = items.filter(i => i.declaredAmount > 0);
    if (validItems.length === 0) {
      toast.error('Please add at least one declaration with amount > 0');
      return;
    }

    setSaving(true);
    try {
      await payrollApi.saveMyTaxDeclaration({
        financialYear: formFY,
        items: validItems,
      });
      toast.success('Tax declaration submitted successfully');
      setFormOpen(false);
      void loadDeclarations();
    } catch {
      toast.error('Failed to save tax declaration');
    } finally {
      setSaving(false);
    }
  };

  const totalDeclared = items.reduce((sum, item) => sum + (item.declaredAmount || 0), 0);

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight={700}>My Tax Declarations</Typography>
        <Stack direction="row" spacing={2}>
          <TextField
            select
            size="small"
            label="Financial Year"
            value={financialYear}
            onChange={(e) => setFinancialYear(e.target.value)}
            sx={{ width: 180 }}
          >
            <MenuItem value="">All</MenuItem>
            {fyOptions.map((fy) => (
              <MenuItem key={fy} value={fy}>{fy}</MenuItem>
            ))}
          </TextField>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenForm}>
            New Declaration
          </Button>
        </Stack>
      </Stack>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Financial Year</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Items</TableCell>
                  <TableCell align="right">Total Declared</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {declarations.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">No tax declarations found. Click "New Declaration" to get started.</TableCell>
                  </TableRow>
                ) : (
                  declarations.map((dec) => (
                    <TableRow key={dec.id}>
                      <TableCell>{dec.financialYear}</TableCell>
                      <TableCell>
                        <Chip
                          label={TaxDeclarationStatusLabels[dec.status]}
                          color={TaxDeclarationStatusColors[dec.status]}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">{dec.items.length}</TableCell>
                      <TableCell align="right">₹{(dec.totalDeclaredAmount ?? dec.items.reduce((s, i) => s + i.declaredAmount, 0)).toLocaleString('en-IN')}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography variant="h6" fontWeight={700}>New Tax Declaration</Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <TextField
              select
              fullWidth
              size="small"
              label="Financial Year"
              value={formFY}
              onChange={(e) => setFormFY(e.target.value)}
              sx={{ mb: 3 }}
            >
              {fyOptions.map((fy) => (
                <MenuItem key={fy} value={fy}>{fy}</MenuItem>
              ))}
            </TextField>

            <Typography variant="subtitle1" fontWeight={600} mb={2}>Declaration Items</Typography>

            {items.map((item, index) => (
              <Box key={index} sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                <TextField
                  select
                  size="small"
                  label="Section"
                  value={item.section}
                  onChange={(e) => handleItemChange(index, 'section', e.target.value)}
                  sx={{ width: 280 }}
                >
                  {TAX_SECTIONS.map((s) => (
                    <MenuItem key={s.value} value={s.value}>
                      {s.label}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  size="small"
                  label="Description"
                  value={item.description}
                  onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                  sx={{ flex: 1 }}
                />
                <TextField
                  size="small"
                  label="Amount (₹)"
                  type="number"
                  value={item.declaredAmount || ''}
                  onChange={(e) => handleItemChange(index, 'declaredAmount', Number(e.target.value))}
                  sx={{ width: 150 }}
                  inputProps={{ min: 0 }}
                />
                <IconButton
                  color="error"
                  size="small"
                  onClick={() => handleRemoveItem(index)}
                  disabled={items.length === 1}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            ))}

            <Button size="small" startIcon={<AddIcon />} onClick={handleAddItem} sx={{ mb: 2 }}>
              Add Item
            </Button>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Typography variant="subtitle1" fontWeight={700}>
                Total: ₹{totalDeclared.toLocaleString('en-IN')}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Submit Declaration'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MyTaxDeclarationsPage;
